import { useContext, useEffect, useState } from 'react';
import { Buttons, FlexContainer, ModalContext, Spinner, Text } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import useTranslation from 'Hooks/useTranslation';
import { useFetchGetPreviousYearsPercentage } from 'Containers/PreviousYears/hooks';
import { useGetInfoResultUser } from 'Containers/Result/hooks';
import YearsBanner from 'Containers/PreFiling/components/YearsBanner';
import YearsModal from 'Containers/PreFiling/components/YearsModal';
import { useFetchPreviousYear } from 'Containers/Dashboard/hooks';
import { ResultTypes } from 'Types/common';
import { YearsInfoCompensation } from '../YearsInfoCompensation';
import * as Styled from './styles';

export const YearsInfo = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.FILING);
  const { showModal } = useContext(ModalContext);
  const [fee, setFee] = useState(0);
  const { mutate, isLoading, data: price } = useFetchGetPreviousYearsPercentage();
  const resultInfo = useGetInfoResultUser();
  const { data, isLoading: isLoadingPreviousYear } = useFetchPreviousYear();
  const records = data?.records;

  const isForCompensation = resultInfo?.taxReturnType === ResultTypes.toPay && resultInfo.mandatory;

  useEffect(() => {
    if (data) {
      const initialAmount = Math.abs(data.amountPending ?? 0);
      if (initialAmount > 0 && !isForCompensation) {
        mutate({ amount: initialAmount * 100 });
      }
    }
  }, [data]);

  useEffect(() => {
    if (price?.data?.item?.discountedPrice != null) {
      const newFee = Number(price.data.item.discountedPrice) / 100;
      setFee(newFee);
    }
  }, [price?.data?.item?.discountedPrice]);

  const handleClickTiny = (): void => {
    showModal({
      comp: <YearsModal />,
    });
  };

  const recordsRecoverables = records
    ?.filter(record => record.isRecoverable)
    .sort((a, b) => Number(a.taxYear) - Number(b.taxYear));

  return isLoadingPreviousYear ? (
    <FlexContainer direction="column" fullWidth fullHeight justify="center" align="center">
      <Spinner size="M" mode="primary" />
    </FlexContainer>
  ) : isForCompensation && data ? (
    <YearsInfoCompensation resultInfo={resultInfo} data={data} />
  ) : (
    <FlexContainer direction="column" fullWidth align="center" gap="dimension_16">
      <Styled.YearsContent
        direction="column"
        gap="dimension_08"
        fullWidth
        justify="center"
        align="center"
      >
        <Text type="paragraphs" color="grey" size="XS_M_S">
          {t('PreFiling.years.info')}
        </Text>
        <Styled.YearsContainer gap="dimension_08" fullWidth justify="center" align="center">
          {recordsRecoverables?.map(
            record =>
              record.isRecoverable && (
                <Styled.YearsInfoContainer
                  justify="center"
                  align="center"
                  direction="column"
                  gap="dimension_08"
                  $itemsLength={recordsRecoverables?.length}
                  key={record.id}
                >
                  <Text type="paragraphs" color="dark" size="S_M_S">
                    {record.taxYear}
                  </Text>
                  <Text type="paragraphs" color="success_strong" size="S_L_S">
                    {YayoyakiFormatter.formatAmountMxn(Math.abs(Number(record.amountTotal)))}
                  </Text>
                </Styled.YearsInfoContainer>
              )
          )}
        </Styled.YearsContainer>
        {recordsRecoverables && recordsRecoverables?.length > 1 && (
          <Styled.TotalRecoveredContainer justify="space-between" align="center">
            <Text type="paragraphs" color="success_strong" size="S_L_S">
              {t('PreFiling.years.amount.total')}
            </Text>
            <Text type="paragraphs" color="success_strong" size="S_L_S">
              {YayoyakiFormatter.formatAmountMxn(data?.amountPending ?? 0)}
            </Text>
          </Styled.TotalRecoveredContainer>
        )}
      </Styled.YearsContent>
      <Buttons.Tiny label={t('PreFiling.years.help.button')} onClick={handleClickTiny} />
      <Styled.PreviousYearsFeeContainer justify="space-between" align="center">
        <Text type="paragraphs" color="dark" size="S_L_S">
          {t('PreFiling.years.fee')}
        </Text>
        {isLoading ? (
          <Spinner size="S" mode="primary" />
        ) : (
          <Text type="paragraphs" color="dark" size="M_XL_S">
            {YayoyakiFormatter.formatAmountMxn(Math.abs(fee ?? 0))}
          </Text>
        )}
      </Styled.PreviousYearsFeeContainer>
      <YearsBanner data={resultInfo} />
    </FlexContainer>
  );
};
